import { z } from '@harvestiq/zod';
import {
  GeneratedAlways,
  Selectable,
  Insertable,
  Updateable,
  ColumnType,
} from 'kysely';
import {
  RmaEntityTypeCodes,
  RmaProgramIndicatorCodes,
  RmaTaxIdTypes,
} from '@harvestiq/constants';
import { zDecimalRequired, zTimestamp } from '@harvestiq/zod';
import { expectTypeOf } from 'expect-type';
import {
  DbColumnDecimalNotNull,
  DbTimestampGenerated,
  DbTimestampNullable,
} from '@harvestiq/utils';

export interface EntitiesTable {
  id: GeneratedAlways<string>;
  ownerId: ColumnType<string | null>;
  agentId: ColumnType<string | null>;
  type: ColumnType<RmaEntityTypeCodes>;
  ownershipShare: DbColumnDecimalNotNull;
  taxId: ColumnType<string | null>;
  taxIdType: ColumnType<RmaTaxIdTypes | null>;
  email: ColumnType<string | null>;
  addressLine1: ColumnType<string | null>;
  addressLine2: ColumnType<string | null>;
  city: ColumnType<string | null>;
  stateAbbreviation: ColumnType<string | null>;
  zip: ColumnType<string | null>;
  zipExtensionCode: ColumnType<string | null>;
  phone: ColumnType<string | null>;
  phoneNumberExceptionCode: ColumnType<string | null>;
  phoneExtensionNumber: ColumnType<string | null>;
  internationalAddress: ColumnType<string | null>;
  internationalCountryCode: ColumnType<string | null>;
  internationalPhoneCountryCode: ColumnType<string | null>;
  programIndicator: ColumnType<RmaProgramIndicatorCodes | null>;
  isPre2026BeginningFarmerRancher: ColumnType<
    boolean,
    boolean | undefined,
    boolean
  >;
  isVeteranFarmerRancher: ColumnType<boolean, boolean | undefined, boolean>;
  beginningFarmerRancherCropYearCount: ColumnType<
    number | null,
    number | null | undefined,
    number | null
  >;
  firstName: ColumnType<string | null>;
  middleName: ColumnType<string | null>;
  lastName: ColumnType<string | null>;
  suffix: ColumnType<string | null>;
  title: ColumnType<string | null>;
  picCode: ColumnType<string | null>;
  locationStateCode: ColumnType<string | null>;
  authorizedRepPoa: ColumnType<string | null>;
  stateOfIncorporation: ColumnType<string | null>;
  businessName: ColumnType<string | null>;
  importSourceId: ColumnType<string | null>;
  mergedIntoEntityId: ColumnType<string | null>;
  createdAt: DbTimestampGenerated;
  updatedAt: DbTimestampGenerated;
  deletedAt: DbTimestampNullable;
}

export type DbEntity = Selectable<EntitiesTable>;
export type DbEntityInsert = Insertable<EntitiesTable>;
export type DbEntityUpdate = Updateable<EntitiesTable>;

export const dbEntitySchema = z.object({
  id: z.string().uuid(),
  ownerId: z.string().uuid().nullable(),
  agentId: z.string().uuid().nullable(),
  type: z.nativeEnum(RmaEntityTypeCodes),
  ownershipShare: zDecimalRequired(),
  taxId: z.string().trim().nullable(),
  taxIdType: z.nativeEnum(RmaTaxIdTypes).nullable(),
  email: z.string().trim().nullable(),
  addressLine1: z.string().trim().nullable(),
  addressLine2: z.string().trim().nullable(),
  city: z.string().trim().nullable(),
  stateAbbreviation: z.string().trim().nullable(),
  zip: z.string().trim().nullable(),
  zipExtensionCode: z.string().trim().nullable(),
  phone: z.string().trim().nullable(),
  phoneNumberExceptionCode: z.string().trim().nullable(),
  phoneExtensionNumber: z.string().trim().nullable(),
  internationalAddress: z.string().trim().nullable(),
  internationalCountryCode: z.string().trim().nullable(),
  internationalPhoneCountryCode: z.string().trim().nullable(),
  programIndicator: z.nativeEnum(RmaProgramIndicatorCodes).nullable(),
  isPre2026BeginningFarmerRancher: z.boolean(),
  isVeteranFarmerRancher: z.boolean(),
  beginningFarmerRancherCropYearCount: z.number().nullable(),
  firstName: z.string().trim().nullable(),
  middleName: z.string().trim().nullable(),
  lastName: z.string().trim().nullable(),
  suffix: z.string().trim().nullable(),
  title: z.string().trim().nullable(),
  picCode: z.string().trim().nullable(),
  locationStateCode: z.string().trim().nullable(),
  authorizedRepPoa: z.string().trim().nullable(),
  stateOfIncorporation: z.string().trim().nullable(),
  businessName: z.string().trim().nullable(),
  importSourceId: z.string().trim().nullable(),
  mergedIntoEntityId: z.string().trim().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable(),
});
expectTypeOf<DbEntity>().toMatchTypeOf<z.output<typeof dbEntitySchema>>();

export const dbEntityInsertSchema = dbEntitySchema
  .omit({
    id: true,
  })
  .partial()
  .extend({
    ownershipShare: zDecimalRequired(),
    type: z.nativeEnum(RmaEntityTypeCodes),
    taxIdType: z.nativeEnum(RmaTaxIdTypes).nullish(),
    createdAt: zTimestamp().optional(),
    updatedAt: zTimestamp().optional(),
    deletedAt: zTimestamp().nullable().optional(),
  });
type CreateEntity = z.input<typeof dbEntityInsertSchema>;
expectTypeOf<keyof CreateEntity>().toEqualTypeOf<keyof DbEntityInsert>();
expectTypeOf<DbEntityInsert>().toMatchTypeOf<CreateEntity>();

export const dbEntityUpdateSchema = dbEntityInsertSchema.partial();
type UpdateEntity = z.input<typeof dbEntityUpdateSchema>;

expectTypeOf<keyof UpdateEntity>().toEqualTypeOf<keyof DbEntityUpdate>();
expectTypeOf<DbEntityUpdate>().toMatchTypeOf<UpdateEntity>();

// Remove the sensitive fields from the entity
export const safeDbEntitySchema = dbEntitySchema.omit({
  taxId: true,
});
export type SafeDbEntity = Selectable<z.infer<typeof safeDbEntitySchema>>;
